(() => {
  if (window.__dealsBannerGridSliderLoaded) return;

  window.__dealsBannerGridSliderLoaded = true;

  const selectors = {
    section: '[data-deals-banner-grid]',
    track: '[data-deals-slider-track]',
    slide: '[data-deals-slide]',
    dot: '[data-deals-dot]',
  };

  const classes = {
    ready: 'is-slider-ready',
    active: 'is-active',
  };

  const instances = new WeakMap();
  const mobileMedia = window.matchMedia('(max-width: 749px)');
  const reducedMotionMedia = window.matchMedia('(prefers-reduced-motion: reduce)');

  class DealsBannerGridSlider {
    constructor(section) {
      this.section = section;
      this.track = section.querySelector(selectors.track);
      this.slides = Array.from(section.querySelectorAll(selectors.slide));
      this.dots = Array.from(section.querySelectorAll(selectors.dot));

      this.autoplayEnabled = section.dataset.autoplay === 'true';
      this.autoplaySpeed = Number(section.dataset.autoplaySpeed) || 4000;

      this.currentIndex = 0;
      this.autoplayTimer = null;
      this.scrollFrame = null;
      this.resizeFrame = null;
      this.isPointerDown = false;
      this.isVisible = true;
      this.observer = null;

      if (!this.track || this.slides.length <= 1) return;

      this.handleScroll = this.handleScroll.bind(this);
      this.handleResize = this.handleResize.bind(this);
      this.handlePointerDown = this.handlePointerDown.bind(this);
      this.handlePointerUp = this.handlePointerUp.bind(this);
      this.handleFocusIn = this.handleFocusIn.bind(this);
      this.handleFocusOut = this.handleFocusOut.bind(this);
      this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
      this.handleMediaChange = this.handleMediaChange.bind(this);

      this.bindEvents();
      this.observeVisibility();
      this.section.classList.add(classes.ready);
      this.sync();
      this.startAutoplay();
    }

    bindEvents() {
      this.track.addEventListener('scroll', this.handleScroll, { passive: true });
      this.track.addEventListener('pointerdown', this.handlePointerDown, { passive: true });
      this.track.addEventListener('pointerup', this.handlePointerUp, { passive: true });
      this.track.addEventListener('pointercancel', this.handlePointerUp, { passive: true });
      this.track.addEventListener('focusin', this.handleFocusIn);
      this.track.addEventListener('focusout', this.handleFocusOut);

      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => {
          const index = Number(dot.dataset.slideIndex);

          if (Number.isNaN(index)) return;

          this.stopAutoplay();
          this.goToSlide(index);
          this.startAutoplay();
        });
      });

      window.addEventListener('resize', this.handleResize);
      document.addEventListener('visibilitychange', this.handleVisibilityChange);

      if (mobileMedia.addEventListener) {
        mobileMedia.addEventListener('change', this.handleMediaChange);
        reducedMotionMedia.addEventListener('change', this.handleMediaChange);
      } else {
        mobileMedia.addListener(this.handleMediaChange);
        reducedMotionMedia.addListener(this.handleMediaChange);
      }
    }

    observeVisibility() {
      if (!('IntersectionObserver' in window)) return;

      this.observer = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];

          this.isVisible = Boolean(entry && entry.isIntersecting);

          if (this.isVisible) {
            this.startAutoplay();
          } else {
            this.stopAutoplay();
          }
        },
        {
          threshold: 0.25,
        }
      );

      this.observer.observe(this.section);
    }

    isMobile() {
      return mobileMedia.matches;
    }

    canAutoplay() {
      return (
        this.isMobile() &&
        this.autoplayEnabled &&
        this.isVisible &&
        !this.isPointerDown &&
        !document.hidden &&
        !reducedMotionMedia.matches
      );
    }

    getSlideOffset(index) {
      const slide = this.slides[index];

      if (!slide) return 0;

      return slide.offsetLeft - this.track.offsetLeft;
    }

    getClosestSlideIndex() {
      const currentScroll = this.track.scrollLeft;

      return this.slides.reduce(
        (closest, slide, index) => {
          const slideOffset = slide.offsetLeft - this.track.offsetLeft;
          const distance = Math.abs(currentScroll - slideOffset);

          if (distance < closest.distance) {
            return { index, distance };
          }

          return closest;
        },
        { index: 0, distance: Infinity }
      ).index;
    }

    normalizeIndex(index) {
      if (index >= this.slides.length) return 0;
      if (index < 0) return this.slides.length - 1;

      return index;
    }

    goToSlide(index, behavior = 'smooth') {
      if (!this.isMobile()) return;

      const nextIndex = this.normalizeIndex(index);

      this.track.scrollTo({
        left: this.getSlideOffset(nextIndex),
        behavior: reducedMotionMedia.matches ? 'auto' : behavior,
      });

      this.currentIndex = nextIndex;
      this.updatePagination();
    }

    goToNextSlide() {
      this.goToSlide(this.currentIndex + 1);
    }

    sync() {
      this.currentIndex = this.getClosestSlideIndex();
      this.updatePagination();
    }

    updatePagination() {
      this.dots.forEach((dot, index) => {
        const isActive = index === this.currentIndex;

        dot.classList.toggle(classes.active, isActive);
        dot.setAttribute('aria-current', isActive ? 'true' : 'false');
      });
    }

    handleScroll() {
      if (this.scrollFrame) return;

      this.scrollFrame = window.requestAnimationFrame(() => {
        this.sync();
        this.scrollFrame = null;
      });
    }

    handleResize() {
      if (this.resizeFrame) return;

      this.resizeFrame = window.requestAnimationFrame(() => {
        this.sync();

        if (this.isMobile()) {
          this.goToSlide(this.currentIndex, 'auto');
          this.startAutoplay();
        } else {
          this.stopAutoplay();
        }

        this.resizeFrame = null;
      });
    }

    handleMediaChange() {
      this.handleResize();
    }

    handlePointerDown() {
      this.isPointerDown = true;
      this.stopAutoplay();
    }

    handlePointerUp() {
      this.isPointerDown = false;
      this.sync();
      this.startAutoplay();
    }

    handleFocusIn() {
      this.stopAutoplay();
    }

    handleFocusOut() {
      this.startAutoplay();
    }

    handleVisibilityChange() {
      if (document.hidden) {
        this.stopAutoplay();
      } else {
        this.startAutoplay();
      }
    }

    startAutoplay() {
      this.stopAutoplay();

      if (!this.canAutoplay()) return;

      this.autoplayTimer = window.setTimeout(() => {
        this.goToNextSlide();
        this.startAutoplay();
      }, this.autoplaySpeed);
    }

    stopAutoplay() {
      if (!this.autoplayTimer) return;

      window.clearTimeout(this.autoplayTimer);
      this.autoplayTimer = null;
    }

    destroy() {
      this.stopAutoplay();

      this.section.classList.remove(classes.ready);

      if (this.scrollFrame) {
        window.cancelAnimationFrame(this.scrollFrame);
      }

      if (this.resizeFrame) {
        window.cancelAnimationFrame(this.resizeFrame);
      }

      if (this.observer) {
        this.observer.disconnect();
      }

      this.track.removeEventListener('scroll', this.handleScroll);
      this.track.removeEventListener('pointerdown', this.handlePointerDown);
      this.track.removeEventListener('pointerup', this.handlePointerUp);
      this.track.removeEventListener('pointercancel', this.handlePointerUp);
      this.track.removeEventListener('focusin', this.handleFocusIn);
      this.track.removeEventListener('focusout', this.handleFocusOut);

      window.removeEventListener('resize', this.handleResize);
      document.removeEventListener('visibilitychange', this.handleVisibilityChange);

      if (mobileMedia.removeEventListener) {
        mobileMedia.removeEventListener('change', this.handleMediaChange);
        reducedMotionMedia.removeEventListener('change', this.handleMediaChange);
      } else {
        mobileMedia.removeListener(this.handleMediaChange);
        reducedMotionMedia.removeListener(this.handleMediaChange);
      }
    }
  }

  const findSection = (target) => {
    if (!target) return null;

    if (target.matches && target.matches(selectors.section)) {
      return target;
    }

    return target.querySelector ? target.querySelector(selectors.section) : null;
  };

  const initSection = (section) => {
    if (!section || instances.has(section)) return;

    const instance = new DealsBannerGridSlider(section);
    instances.set(section, instance);
  };

  const destroySection = (section) => {
    const instance = instances.get(section);

    if (!instance) return;

    instance.destroy();
    instances.delete(section);
  };

  const initAllSections = () => {
    document.querySelectorAll(selectors.section).forEach(initSection);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAllSections, { once: true });
  } else {
    initAllSections();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const section = findSection(event.target);

    if (section) initSection(section);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const section = findSection(event.target);

    if (section) destroySection(section);
  });

  document.addEventListener('shopify:block:select', (event) => {
    const section = event.target.closest(selectors.section);
    const slide = event.target.closest(selectors.slide);
    const instance = instances.get(section);

    if (!instance || !slide) return;

    const index = instance.slides.indexOf(slide);

    if (index < 0) return;

    instance.stopAutoplay();
    instance.goToSlide(index);
  });
})();